/**
 * Generate Jig auth-and-routing module.
 * Builds authenticated request headers and handles Spark endpoint routing with
 * fallback across the supported backend paths.
 */
import { AppConst, UtilsConst } from '@/constants'
import { getInstance } from '@/plugins/auth0'
import type { AxiosRequestConfig } from 'axios'
import axios from 'axios'
import type { SparkRouteCandidate } from './generate-jig.types'

export function getRequiredAuthContext(tenantId: number): { accessToken: string; tenantId: string } {
  const authService = getInstance()
  const accessToken = authService && typeof authService.accessToken === 'string' ? authService.accessToken : ''
  const tenantIdStr = tenantId > 0 ? String(tenantId) : ''

  if (!accessToken) {
    throw new Error('Missing access token for Jig API requests')
  }

  if (!tenantIdStr) {
    throw new Error('Missing tenant context for Jig API requests')
  }

  return { accessToken, tenantId: tenantIdStr }
}

export function ensureAuthContext(tenantId: number): void {
  getRequiredAuthContext(tenantId)
}

export function buildAuthHeaders(tenantId: number, includeAppName: boolean, extraHeaders: Record<string, string> = {}): Record<string, string> {
  const authService = getInstance()
  const headers: Record<string, string> = {}

  if (authService && authService.accessToken) {
    headers.Authorization = 'Bearer ' + authService.accessToken
  }

  if (tenantId > 0) {
    headers[UtilsConst.RequestHeaders.CustomHeaderKeyTenantId] = String(tenantId)
  }

  if (includeAppName) {
    headers[UtilsConst.RequestHeaders.CustomHeaderKeyAppName] = AppConst.appName
  }

  Object.keys(extraHeaders).forEach((key: string) => {
    headers[key] = extraHeaders[key]
  })

  return headers
}

export function buildJigApiHeaders(tenantId: number, includeAppName: boolean, extraHeaders: Record<string, string> = {}): Record<string, string> {
  const authContext = getRequiredAuthContext(tenantId)
  const headers: Record<string, string> = {
    Authorization: 'Bearer ' + authContext.accessToken,
    [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: authContext.tenantId,
  }

  if (includeAppName) {
    headers[UtilsConst.RequestHeaders.CustomHeaderKeyAppName] = AppConst.appName
  }

  Object.keys(extraHeaders).forEach((key: string) => {
    headers[key] = extraHeaders[key]
  })

  return headers
}

export function getSparkRouteCandidates(): SparkRouteCandidate[] {
  return [
    { key: 'aiComplete', path: AppConst.APIEndpoint.sparkV1Url + '/ai/complete' },
    // { key: 'geminiImage', path: AppConst.APIEndpoint.sparkV1Url + '/gemini/generate-image' },
  ]
}

export function buildSparkUrl(apiDomain: string, path: string): string {
  return apiDomain + AppConst.APIEndpoint.senseV1Url + path
}

export async function postToSpark(
  apiDomain: string,
  tenantId: number,
  path: string,
  payload: any,
  responseType?: 'json' | 'arraybuffer',
  requestConfig: AxiosRequestConfig = {}
): Promise<any> {
  const requestHeaders = requestConfig.headers ? requestConfig.headers as Record<string, string> : {}

  return axios.post(
    buildSparkUrl(apiDomain, path),
    payload,
    {
      ...requestConfig,
      responseType: responseType || requestConfig.responseType || 'json',
      headers: {
        ...requestHeaders,
        ...buildAuthHeaders(tenantId, false),
      },
    }
  )
}

/**
 * Attempts the named Spark route against each configured candidate path until one
 * succeeds, preserving the last failure when all candidates reject.
 */
export async function postToSparkWithFallback(
  apiDomain: string,
  tenantId: number,
  routeKey: string,
  payload: any,
  responseType?: 'json' | 'arraybuffer',
  requestConfig: AxiosRequestConfig = {}
): Promise<any> {
  const routes = getSparkRouteCandidates().filter(function (item: SparkRouteCandidate) {
    return item.key === routeKey
  })

  let lastError: any = null

  for (let i = 0; i < routes.length; i++) {
    const route = routes[i]

    try {
      return await postToSpark(apiDomain, tenantId, route.path, payload, responseType, requestConfig)
    } catch (error) {
      lastError = error
    }
  }

  throw lastError
}
